import type { AxiosError } from 'axios';
import axios from 'axios';
import type { Storage } from '../storage';
import { getLocalStorage, getCookieStorage, CookieStorage, COOKIE_PREFIX_KEY } from '../storage';
import type { Subject, Observable } from 'rxjs';
import { ReplaySubject } from 'rxjs';
import qs from 'query-string';
import type { Logger } from '../logging';
import { createLoggerObject } from './utils';
import globalWindow from '@shared/core/globals';

const FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS = 60;
// WHY a window and not a fixed offset: every tab and device holding the same session reads the same
// expiry from the token, so a fixed offset makes all of them renew in the same second. Auth0 allows
// 10 /delegation calls per user per IP per minute; a user with a few busy tabs tripped it, got 429s
// and was logged out. The background refresh fires at a random point 60..120 seconds before expiry
// instead, drawn once per access token (see backgroundRefreshSecondsBeforeExpiry).
const BACKGROUND_REFRESH_WINDOW_MIN_SECONDS = FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS;
const BACKGROUND_REFRESH_WINDOW_MAX_SECONDS = 120;
// Minimum TTL for the SSR / first-render eager auth path (hasValidTokenForEagerAuth, parseAuthCookie).
// No longer the background refresh offset, but kept so SSR and CSR keep agreeing on the threshold.
const BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS = 1000; // 16.67 minutes

// WHY: the user-token signing key was rotated on 2026-10-30, and the old key is never accepted again. Tokens
// issued before then still look valid here (24h `exp`), so pages kept sending them, the API rejected them,
// and admin routes bounced to /login and back without ever using the refresh token. Any token issued before
// this moment is treated as expired and renewed with the refresh token instead. Compared against the token's
// raw `iat` (server clock), never offset-adjusted, so a skewed device clock cannot change the answer. Set a few
// minutes after the rotation, as some services only picked up the new key when they restarted.
// Safe to remove once every token issued before it has expired (after 2026-10-31 17:50 UTC).
const KEY_ROTATION_CUTOFF_IAT_SECONDS = 1793382600; // 2026-10-30T17:50:00Z

/** True when the token carries an `iat` from before the key rotation. A token without a numeric `iat` is left to the `exp` checks. */
const isIssuedBeforeKeyRotation = (decodedToken: DecodedToken): boolean => typeof decodedToken?.iat === 'number' && decodedToken.iat < KEY_ROTATION_CUTOFF_IAT_SECONDS;

const SSR_TOKEN_KEY = 'SsrAuthToken';
const LOCAL_STORAGE_TOKEN_KEY = 'secrets';
export const SSR_TOKEN_COOKIE = COOKIE_PREFIX_KEY + SSR_TOKEN_KEY;

import { getRetryAfterMs, isRateLimited } from './auth0.retry';
import { windowExists } from '@shared/utils/windowExists';
import { addAction } from '@shared/utils/logger';
import { decodeBase64ToString } from '@shared/utils/base64Codec';
import { peekRefreshAuthDomain } from '@static/js/authDomainFlag';
import { getAuthDomain } from '@static/js/authDomain';
import { config } from '@static/js/env.config';

export interface AccessToken
  extends Readonly<{
    id_token: string;
    bearer_type: string;
    expires_in: number;
  }> {}

interface Secrets {
  accessToken?: AccessToken;
  accessTokenClientId?: string;
  offset?: number;
  /** Required when requesting a new Access Token w/o re-authenticating the user. */
  refreshToken?: string;
  refreshTokenClientId?: string;
}

interface PersistedSecrets extends Pick<Secrets, 'accessTokenClientId' | 'offset' | 'refreshToken' | 'refreshTokenClientId'> {
  accessToken?: string;
}

export interface DecodedToken
  extends Readonly<{
    aud: string;
    azp: string;
    exp: number;
    iat: number;
    iss: string;
    sub: string;
    acr?: string;
    amr?: string[];
  }> {}

/**
 * Decodes a JWT's payload segment into its claims object (P6/PRNT-4559).
 *
 * Single path for both client and server: the shared tolerant codec accepts
 * base64url characters ('-'/'_') and missing padding — JWT payloads are
 * base64url, which the former client-side raw `atob` branch rejected.
 * Replaces the old `windowExists()` client/server split (strict `atob` vs
 * `Buffer.from(..., 'base64')`).
 *
 * Does NOT verify the signature — the result is untrusted claims. Throws on
 * malformed input; call sites wrap it in try/catch.
 */
export const decodeJwtPayload = (idToken: string): DecodedToken => JSON.parse(decodeBase64ToString(idToken.split('.')[1]));

const category = 'joy-web.Auth';

/**
 * The browser's view of the current access token at the moment a renewal event is logged. Server logs
 * cannot show this: whether a token "looks expired" depends on this device's clock and the stored offset.
 * - `timeLeft`: seconds until expiry as getTimeLeftInSecondsBeforeExpiration computes it (undefined with no decodable token).
 * - `offset`: `Date.now() - serverTime` in ms, recorded at login and corrected from every renewed token's `iat`
 *   (undefined when never recorded).
 * - `clientNow`: `Date.now()` on this device, ms.
 * - `iat` / `exp`: the token's own claims, seconds.
 */
type RenewalDiagnostics = Readonly<{
  timeLeft: number | undefined;
  offset: number | undefined;
  clientNow: number;
  iat: number | undefined;
  exp: number | undefined;
}>;

const initializeTokenLogger = createLoggerObject({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refreshTokenError: (msg: string, error: unknown, extra: any = {}) => ({
    category,
    action: 'RefreshToken',
    actionType: 'error',
    extraInfo: { message: msg, error, ...extra },
  }),
  ensureSecretsFormatError: (error: unknown) => ({
    category,
    action: 'EnsureSecretsFormat',
    actionType: 'error',
    extraInfo: { error },
  }),
  refreshTokenTimedOut: (domain: string) => ({
    category,
    action: 'RefreshTokenTimedOut',
    extraInfo: { domain },
  }),
  refreshTokenRateLimited: (domain: string, retryInMs: number, diagnostics: RenewalDiagnostics) => ({
    category,
    action: 'RefreshTokenRateLimited',
    extraInfo: { domain, retryInMs, ...diagnostics },
  }),
  // A renewal just succeeded, yet by this device's clock and offset the new token is already inside the
  // foreground buffer, so the next token read will renew again. Diagnostics for the renewal loop a few
  // devices a day run into Auth0's rate limit with; an error so it reaches the error pipeline.
  refreshTokenLooksExpired: (domain: string, expiresIn: number | undefined, tokenAgeSeconds: number | undefined, diagnostics: RenewalDiagnostics) => ({
    category,
    action: 'RefreshTokenLooksExpired',
    actionType: 'error',
    extraInfo: { domain, expiresIn, tokenAgeSeconds, ...diagnostics },
  }),
});

// Wait this long before refreshing again when a 429 came without a usable Retry-After.
const RATE_LIMITED_RETRY_IN_MS = 30_000;
const RATE_LIMITED_RETRY_JITTER_MS = 1000;

// A /delegation request still unanswered after this long is abandoned, so a hung connection cannot
// hold every later caller behind the shared in-flight renewal: they start a fresh one instead.
const RENEWAL_REQUEST_TIMEOUT_MS = 15_000;

class RenewalTimeoutError extends Error {
  constructor(public readonly host: string) {
    super(`No answer from ${host}/delegation within ${RENEWAL_REQUEST_TIMEOUT_MS}ms`);
    this.name = 'RenewalTimeoutError';
  }
}

// Rejects with RenewalTimeoutError once the timeout passes. A late answer from the request is ignored:
// it never settles the returned promise again, and a late rejection is swallowed rather than unhandled.
const withRenewalTimeout = <T>(request: Promise<T>, host: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new RenewalTimeoutError(host)), RENEWAL_REQUEST_TIMEOUT_MS);
    request.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      err => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });

/**
 * TokenService manages authentication secrets (access tokens, refresh tokens, client IDs, clock offset).
 *
 * ## Storage Strategy: Cookie-Primary with localStorage Sync
 *
 * **Primary Storage:** Cookies (`joySsrAuthToken`)
 * - Single source of truth for auth secrets
 * - Enables SSR to authenticate GraphQL requests
 * - Supports token refresh on both client and server
 * - Enhanced security with `SameSite=Strict` flag
 *
 * **Secondary Storage:** localStorage (`secrets`) - TEMPORARY
 * - Maintained for backwards compatibility during migration period
 * - All writes go to BOTH storages (dual-write pattern)
 * - Reconciliation logic migrates incomplete cookies from localStorage
 * - TODO: Remove after 1-2 months of production confidence
 *
 * ## Why Cookie-Primary?
 *
 * **Industry Best Practice:**
 * Cookies are the gold standard for storing authentication tokens because:
 * - Native SSR support (automatically sent with HTTP requests)
 * - Security flags (`HttpOnly`, `Secure`, `SameSite`) protect against XSS/CSRF
 * - Browser handles expiration and cleanup automatically
 * - Works seamlessly across client and server contexts
 *
 * **Before (localStorage-primary):**
 * - localStorage was primary, cookies only written after token refresh
 * - Cookies contained ONLY AccessToken (insufficient for client-side operations)
 * - SSR required manual sync between localStorage ↔ cookies
 * - Sync could drift, causing SSR issues
 *
 * **After (cookie-primary):**
 * - Cookies contain FULL Secrets object (refreshToken, clientIds, offset, accessToken)
 * - Eliminates sync issues between storages
 * - SSR always has latest auth state
 * - Enables cookies as single source of truth
 *
 * ## Migration & Reconciliation
 *
 * During the transition period, users may have:
 * - **Partial cookies:** Legacy cookies with only AccessToken (missing refreshToken/clientIds)
 * - **Complete localStorage:** Full secrets in localStorage from before migration
 *
 * **Reconciliation Strategy (Option B - Prefer Complete Secrets):**
 * 1. Read from cookies first (primary storage)
 * 2. If cookie is incomplete, check localStorage for complete secrets
 * 3. If localStorage has complete secrets, migrate them to cookies
 * 4. Continue with complete secrets (seamless for users)
 *
 * **Why No Token Verification Needed:**
 * The reconciliation logic does NOT verify that `id_token` matches between storages because
 * the legacy architecture made token mismatches architecturally impossible:
 * - Cookies only written during token refresh (using same token as localStorage)
 * - Logout cleared both storages atomically
 * - Single controlled login flow via auth callback
 *
 * See "Why Token Mismatch Was Impossible" section in AUTH_TOKEN_FLOW.md for detailed analysis.
 *
 * This ensures users with partial cookies can still refresh tokens and stay logged in.
 *
 * ## Cleanup (Phase 2)
 *
 * After 1-2 months of production confidence:
 * - Remove localStorage dual-write in `writeToSecretsAndAlertListeners()`
 * - Remove localStorage sync in `deleteSecrets()`
 * - Remove reconciliation logic in `collectSavedSecrets()`
 * - Remove `localStorage` field from this class
 * - Cookies become the single source of truth
 *
 * @see {@link doc/guest-site-perf-audit/02-auth-secrets-as-cookie/AUTH_TOKEN_FLOW.md} for detailed migration documentation
 */
export class TokenService {
  private logger: ReturnType<typeof initializeTokenLogger>;
  private readonly accessTokenSubject: Subject<AccessToken | null>;
  // The one renewal in flight for this service; every caller awaits it rather than sending its own.
  private generateAccessTokenPromise: Promise<AccessToken | null> | null = null;
  private refreshTimeoutId: number | undefined;
  // Seconds before expiry at which the current access token's background refresh fires. Drawn once per
  // token: every token read reschedules the timer, and each reschedule must aim at the same moment.
  private backgroundRefresh: { idToken: string; secondsBeforeExpiry: number } | undefined;
  // While set and in the future, renewal is in a cooldown (the proxy throttled us, or was unavailable);
  // no renewal is sent before then, however many token reads ask for one.
  private rateLimitedUntil: number | undefined;
  // Wakes a renewal that is sleeping out the cooldown, so sign-out or a new session need not wait for it.
  private wakeCooldownSleep: (() => void) | undefined;
  private secrets: Secrets = {};
  // The pre-rotation token last reported by reportPreRotationRenewal, so each is reported once.
  private reportedPreRotationIdToken: string | undefined;
  // Last answer of isPreRotationSecrets, keyed by the token it was computed for.
  private preRotationCheck: { idToken: string; preRotation: boolean } | undefined;
  private readonly storage: Storage;
  private readonly localStorage: Storage | undefined;

  constructor(logger: Logger) {
    this.logger = initializeTokenLogger(logger);
    this.accessTokenSubject = new ReplaySubject(1);
    // Cookie-primary storage for SSR consistency and security
    this.storage = getCookieStorage() || getLocalStorage();
    // TODO: Remove localStorage sync after migration confidence (1-2 months post-deployment)
    this.localStorage = getLocalStorage();
    // Start the refresh flag lookup now, so a later refresh can use its answer without waiting.
    peekRefreshAuthDomain();
    this.authenticate();
  }

  public accessTokenObservable(): Observable<AccessToken | null> {
    return this.accessTokenSubject;
  }

  public authenticate(): void {
    this.checkTokenExpirationAndQueueRefresh();
  }

  public getToken(): string | null {
    // collect secrets to ensure the in-memory token matches the one in the storage
    // COMMENTED OUT: Seems like this may be causing a regression in createwedding
    // this.collectSavedSecrets();
    return this.getAccessToken();
  }

  public getTokenAsync = async (): Promise<string | null> => {
    // checkTokenExpirationAndQueueRefresh already collects secrets to ensure the in-memory token matches the one in the storage
    await this.checkTokenExpirationAndQueueRefresh();
    return this.getAccessToken();
  };

  public setToken(token: AccessToken | null, clientId: string, refreshToken?: string, serverTime?: number, options?: { skipStorage?: boolean; offset?: number }) {
    // A new session ends any rate-limit cooldown and wakes a renewal still sleeping on it.
    this.rateLimitedUntil = undefined;
    this.wakeCooldownSleep?.();
    if (options?.skipStorage) {
      // During SSR, populate in-memory secrets only.
      // The SSR LoginManager is short-lived (per-request) and discarded after rendering.
      //
      // Cancel any pending async refresh from the constructor's authenticate() call.
      // The constructor fires checkTokenExpirationAndQueueRefresh() which is async —
      // its .finally() microtask calls collectSavedSecrets() which reads from storage.
      this.generateAccessTokenPromise = null;
      this.clearRefreshTimeoutId();

      this.secrets.accessToken = token || undefined;
      if (refreshToken) {
        this.secrets.refreshToken = refreshToken;
      }
      if (clientId) {
        this.secrets.accessTokenClientId = clientId;
        this.secrets.refreshTokenClientId = clientId;
      }
      // Accept pre-computed offset directly (from the cookie), since serverTime
      // would require reverse-computing Date.now() - offset to pass in.
      if (typeof options.offset === 'number') {
        this.secrets.offset = options.offset;
      } else if (serverTime) {
        this.secrets.offset = Date.now() - serverTime;
      }

      // Clear stale empty secrets the constructor wrote to storage.
      // The constructor's writeToSecretsAndAlertListeners(null) writes "{}" to the
      // module-level MemoryStorage singleton. If we leave it, the .finally() microtask's
      // collectSavedSecrets() would read "{}" and overwrite our seeded in-memory secrets.
      // We clear instead of writing our token because MemoryStorage is shared across all
      // requests on the same server process — writing user tokens would leak auth between
      // requests. Clearing ensures collectSavedSecrets() finds nothing and preserves
      // this.secrets as-is.
      this.deleteSecretsFromStorage();
      if (this.localStorage) {
        this.localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, undefined);
      }

      // Intentionally skipping broadcastTokenUpdate() — during SSR, useEffect subscriptions
      // don't fire so there are no observers. The eager GetMe query reads the token directly
      // via loginManager.getToken(), not through the observable.
    } else {
      // The token's own `iat` is the server clock at login; `serverTime` is only the fallback for a token without one.
      const offsetFromIssuedAt = this.offsetFromIssuedAt(token);
      if (offsetFromIssuedAt !== undefined) this.secrets.offset = offsetFromIssuedAt;
      this.writeToSecretsAndAlertListeners(token, clientId, refreshToken, offsetFromIssuedAt === undefined ? serverTime : undefined);
      this.clearRefreshTimeoutId();
      if (token) {
        this.checkTokenExpirationAndQueueRefresh();
      }
    }
  }

  public deleteSecrets() {
    // Delete from primary storage (cookies)
    this.deleteSecretsFromStorage();

    // Sync deletion to localStorage for backwards compatibility
    // TODO: Remove after migration confidence (1-2 months post-deployment)
    if (this.localStorage) {
      this.localStorage.setItem('secrets', undefined);
    }

    // Forget them in memory too, so a refresh that is still in flight cannot reschedule or write them back.
    this.secrets = {};
    this.rateLimitedUntil = undefined;
    this.wakeCooldownSleep?.();
    this.clearRefreshTimeoutId();
  }

  public hasValidMfaClaim() {
    const idToken = this.getDecodedAccessToken();

    if (idToken == null) {
      return false;
    }

    const { iat, amr } = idToken;
    const fifteenMinutes = 900;

    const hasTokenMFAClaim = Boolean(amr?.includes('mfa'));
    const now = Math.floor(Date.now() / 1000);
    const isIssuedLessThan15Minutes = iat > now - fifteenMinutes;

    return hasTokenMFAClaim && isIssuedLessThan15Minutes;
  }

  private attemptToRefreshTokenAndWrite = (): Promise<AccessToken | null> => {
    // One renewal in flight per tab (per TokenService): a caller asking while one runs awaits that same
    // request. On a busy page every GraphQL request and the refresh timer notice "token needs renewing"
    // in the same moment; each sending its own POST /delegation is what burst past Auth0's rate limit.
    if (this.generateAccessTokenPromise) return this.generateAccessTokenPromise;
    // In a cooldown (throttled, or the proxy was unavailable) and the current token still works: hand it
    // out and leave the retry timer alone. Every GraphQL request asks for a token, so without this an
    // active page would renew at once.
    if (this.isRateLimited() && this.hasUsableAccessToken()) {
      return Promise.resolve(this.secrets.accessToken || null);
    }
    this.clearRefreshTimeoutId();
    // Nothing to renew with (no session yet, or signed out): write what there is and answer at once, without
    // taking the in-flight slot. Otherwise a session set in the same tick would have its first callers
    // join this no-op instead of the renewal it starts.
    if (!this.secrets.accessTokenClientId || !this.secrets.refreshToken) {
      const current = this.secrets.accessToken || null;
      this.writeToSecretsAndAlertListeners(current);
      return Promise.resolve(current);
    }
    // Set when the renewal was throttled or timed out: try again after this delay instead of straight away.
    let retryInMs: number | undefined;
    const renewal = this.renewAccessToken(this.secrets.accessTokenClientId, this.secrets.refreshToken, ms => (retryInMs = ms)).finally(() => {
      // Remove the promise (success, failure or timeout alike) and begin a new refresh cycle.
      if (this.generateAccessTokenPromise === renewal) this.generateAccessTokenPromise = null;
      // Nothing to reschedule once the user has signed out (deleteSecrets) during the wait.
      if (!this.secrets.accessTokenClientId || !this.secrets.refreshToken) return;
      if (retryInMs !== undefined) {
        this.clearRefreshTimeoutId();
        this.refreshTimeoutId = globalWindow.setTimeout?.(() => {
          this.attemptToRefreshTokenAndWrite();
        }, retryInMs);
      } else {
        this.checkTokenExpirationAndQueueRefresh();
      }
    });
    this.generateAccessTokenPromise = renewal;

    // This promise always resolves with either:
    // 1. the new access token
    // 2. the current token (throttled, or no answer in time) or null (refused, or signed out)
    return renewal;
  };

  /**
   * Sends the renewal for `refreshToken` and writes the result once. Resolves the token to hand to callers:
   * the renewed one; the current one when it is still usable and the host throttled us or was unavailable
   * (5xx, timeout, no response); null when the token was refused (4xx) or the user signed out meanwhile. `scheduleRetry` receives a delay whenever the next
   * attempt must wait for it rather than follow the usual expiry-based cycle.
   */
  private async renewAccessToken(clientId: string, refreshToken: string, scheduleRetry: (ms: number) => void): Promise<AccessToken | null> {
    const searchParams = qs.stringify({
      client_id: clientId,
      grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
      target: clientId,
      scope: 'openid',
      api_type: 'app',
      refresh_token: refreshToken,
    });

    // Refresh never waits on Amplitude. A finished flag lookup picks the host; otherwise it goes to this
    // environment's auth proxy. The browser never contacts Auth0 on its own: if the proxy cannot answer,
    // renewal waits for the proxy rather than fall back to another host.
    const domain = peekRefreshAuthDomain() ?? getAuthDomain(config.joyEnvironment);
    const postOnce = async (): Promise<AccessToken | null> => (await withRenewalTimeout(axios.post<AccessToken | null>(`https://${domain}/delegation`, searchParams), domain)).data;

    // Nothing here retries into a rate limit or hammers a down host. Every pass of this loop sends one
    // request; a 429, 5xx, timeout or no response is followed by nothing until a cooldown has passed (the
    // 2s/5s/15s retries of #19234 tripled the burst), so a tab sends at most one request per window.
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Still inside a rate-limit cooldown (a page load after a sleep, or the pass after a 429): wait it out first.
      const cooldownMs = (this.rateLimitedUntil ?? 0) - Date.now();
      if (cooldownMs > 0) await this.sleepThroughCooldown(cooldownMs);
      // Signed out while waiting: nothing to renew, nothing to write.
      if (!this.secrets.refreshToken) return null;
      // A different session was set while waiting: it has its own refresh cycle, so leave it alone.
      if (this.secrets.refreshToken !== refreshToken) return this.secrets.accessToken || null;
      try {
        const renewed = await postOnce();
        this.rateLimitedUntil = undefined;
        // Signed out while the renewal was in flight: do not write the new token back.
        if (!this.secrets.accessTokenClientId) return null;
        // A different session was set while the renewal was in flight (a login in a popup, say): neither this
        // token nor the offset it implies may be written over the new session's.
        if (this.secrets.refreshToken !== refreshToken) return this.secrets.accessToken || null;
        // WHY correct the offset here: the offset recorded at login (Date.now() - serverTime) goes stale when the
        // device clock is corrected later, and it is persisted in the cookie and localStorage across sign-ins. A
        // device in production carried an offset of -38.6 hours while its clock had long been right again, so by
        // that offset every fresh 24-hour token was already 14.6 hours past expiry, and it renewed 16,814 times in
        // two hours. `iat` is the one server timestamp every renewal already carries: it is the server clock at
        // the moment this token was minted, so Date.now() - iat matches the server clock now.
        const correctedOffset = this.offsetFromIssuedAt(renewed);
        if (correctedOffset !== undefined) this.secrets.offset = correctedOffset;
        this.writeToSecretsAndAlertListeners(renewed);
        if (this.logIfRenewedTokenLooksExpired(renewed, domain) || this.isRenewedTokenFromBeforeKeyRotation(renewed)) {
          // Backstop: a token the server minted seconds ago is never renewed again at once, whatever this clock
          // says about it. Keep it, and start the same cooldown a 429 would: token reads hand this token out while
          // it is younger than the cooldown (see hasUsableAccessToken) and the timer sends the next (single)
          // renewal after it. The RefreshTokenLooksExpired event above is the signal that this ran.
          this.rateLimitedUntil = Date.now() + RATE_LIMITED_RETRY_IN_MS;
          scheduleRetry(RATE_LIMITED_RETRY_IN_MS);
        }
        return renewed;
      } catch (err) {
        if (isRateLimited(err)) {
          // The proxy is throttling us, not refusing the token. Keep the refresh token; the user
          // must not be logged out for a 429.
          // Jitter on the default so devices throttled together do not retry together.
          const retryInMs = getRetryAfterMs(err) ?? RATE_LIMITED_RETRY_IN_MS + Math.random() * RATE_LIMITED_RETRY_JITTER_MS;
          this.rateLimitedUntil = Date.now() + retryInMs;
          const diagnostics = this.renewalDiagnostics(this.getDecodedAccessToken());
          this.logger.refreshTokenRateLimited(domain, retryInMs, diagnostics);
          // The Logger above is console-only in the browser; this is what reaches New Relic Browser (PageAction).
          addAction('RefreshTokenRateLimited', { domain, retryInMs, ...diagnostics });
          if (!this.secrets.refreshToken) return null;
          // The current token still works: hand it out now and let the timer renew once the cooldown has passed.
          if (this.hasUsableAccessToken()) {
            scheduleRetry(retryInMs);
            return this.secrets.accessToken || null;
          }
          // Expired: callers keep waiting rather than receive a token GetMe would reject and show as a
          // logout. The top of the loop sleeps through the cooldown (sign-out and a new session wake it),
          // then sends the next single request.
          continue;
        }
        const status = (err as AxiosError).response?.status;
        if (status !== undefined && status < 500) {
          // The proxy answered and refused this token (a 4xx other than 429): the only case that signs out.
          this.logger.refreshTokenError('Server responded, but is unable to refresh the token', err, { domain });
          this.secrets.refreshToken = undefined;
          this.writeToSecretsAndAlertListeners(null);
          return null;
        }
        // The proxy is unavailable (no answer in time, 5xx, no response, or the request could not be made),
        // not refusing the token. Nothing is cleared; the user must not be logged out for an outage.
        if (err instanceof RenewalTimeoutError) {
          this.logger.refreshTokenTimedOut(err.host);
        } else if (status !== undefined) {
          this.logger.refreshTokenError('Server responded, but is unable to refresh the token', err, { domain });
        } else if ((err as AxiosError).request) {
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          this.logger.refreshTokenError('Request was made but no response was received', err, { domain });
        } else {
          // Something happened in setting up the request that triggered an Error
          this.logger.refreshTokenError('Unable to make request', err, { domain });
        }
        if (!this.secrets.refreshToken) return null;
        // A cooldown, not just a timer: without it the next token read on a busy tab would cancel the timer
        // and hit the down host again at once, and an outage would burst like a 429 did.
        this.rateLimitedUntil = Date.now() + RATE_LIMITED_RETRY_IN_MS;
        // The current token still works: callers get it now; the timer tries the proxy again after the cooldown.
        if (this.hasUsableAccessToken()) {
          scheduleRetry(RATE_LIMITED_RETRY_IN_MS);
          return this.secrets.accessToken || null;
        }
        // Expired: callers keep waiting rather than receive a token the API would reject. The top of the loop
        // sleeps through the cooldown (sign-out and a new session wake it), then sends one more request.
      }
    }
  }

  /**
   * The clock offset (`Date.now() - serverTime`, ms) a freshly minted token implies: its `iat` is the server
   * clock at the moment it was issued. Only meaningful for a token that was minted just now (login, renewal);
   * undefined when the token has no readable `iat`, so the caller leaves the stored offset alone.
   */
  private offsetFromIssuedAt(token: AccessToken | null): number | undefined {
    if (!token?.id_token) return undefined;
    try {
      const { iat } = decodeJwtPayload(token.id_token);
      return typeof iat === 'number' ? Date.now() - iat * 1000 : undefined;
    } catch {
      return undefined;
    }
  }

  /** The fields every renewal diagnostic event carries, computed the same way the refresh decisions are. */
  private renewalDiagnostics(decodedToken: DecodedToken | null): RenewalDiagnostics {
    const offset = this.secrets.offset;
    return {
      timeLeft: decodedToken ? this.getTimeLeftInSecondsBeforeExpiration(decodedToken, offset) : undefined,
      offset,
      clientNow: Date.now(),
      iat: decodedToken?.iat,
      exp: decodedToken?.exp,
    };
  }

  /**
   * Right after a renewal is written, decide what the next token read would decide: if the fresh token already
   * has no more than the foreground buffer left, this device would renew again at once. Logs an event with the
   * clock, the offset and the token's claims so the cause can be seen from the logs, and returns true so the
   * caller can keep the token instead of looping on it. False when the token is fine or cannot be decoded.
   */
  private logIfRenewedTokenLooksExpired(renewed: AccessToken | null, domain: string): boolean {
    if (!renewed?.id_token) return false;
    // Decode the response itself, not whatever the secrets hold, so this does not depend on being called
    // right after the write. An undecodable token is diagnosed elsewhere (getDecodedAccessToken); nothing to log here.
    let decodedToken: DecodedToken;
    try {
      decodedToken = decodeJwtPayload(renewed.id_token);
    } catch {
      return false;
    }
    const diagnostics = this.renewalDiagnostics(decodedToken);
    if (diagnostics.timeLeft === undefined || diagnostics.timeLeft > FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS) return false;
    // Seconds since the token was issued, by this device's clock corrected with the stored offset.
    // Seconds old on a healthy device; large or negative when the clock or the offset is off.
    const tokenAgeSeconds = typeof decodedToken.iat === 'number' ? Math.floor(diagnostics.clientNow / 1000 - (diagnostics.offset ?? 0) / 1000) - decodedToken.iat : undefined;
    this.logger.refreshTokenLooksExpired(domain, renewed.expires_in, tokenAgeSeconds, diagnostics);
    // The Logger above is console-only in the browser; this is what reaches New Relic Browser (PageAction).
    addAction('RefreshTokenLooksExpired', { domain, expiresIn: renewed.expires_in, tokenAgeSeconds, ...diagnostics });
    return true;
  }

  /**
   * True when a renewal answered with a token issued before the key rotation (it should never: the server mints a
   * new one). Without this, the next token read would renew it again at once, in a tight loop against /delegation.
   */
  private isRenewedTokenFromBeforeKeyRotation(renewed: AccessToken | null): boolean {
    if (!renewed?.id_token) return false;
    try {
      return isIssuedBeforeKeyRotation(decodeJwtPayload(renewed.id_token));
    } catch {
      return false;
    }
  }

  /**
   * True when these secrets hold an access token issued before the key rotation. Runs on every token read
   * (collectSavedSecrets), so the answer is cached per token and each token is decoded once.
   */
  private isPreRotationSecrets(secrets: Secrets): boolean {
    const idToken = secrets.accessToken?.id_token;
    if (!idToken) return false;
    if (this.preRotationCheck?.idToken !== idToken) {
      let preRotation = false;
      try {
        preRotation = isIssuedBeforeKeyRotation(decodeJwtPayload(idToken));
      } catch {
        preRotation = false;
      }
      this.preRotationCheck = { idToken, preRotation };
    }
    return this.preRotationCheck.preRotation;
  }

  /** One New Relic Browser PageAction per pre-rotation token this tab renews, so the cleanup can be watched. */
  private reportPreRotationRenewal(decodedToken: DecodedToken): void {
    const idToken = this.secrets.accessToken?.id_token;
    if (!idToken || this.reportedPreRotationIdToken === idToken) return;
    this.reportedPreRotationIdToken = idToken;
    addAction('RefreshTokenPreRotation', { iat: decodedToken.iat, exp: decodedToken.exp });
  }

  /** Resolves after the cooldown, or at once when sign-out or a new session wakes it. */
  private sleepThroughCooldown(ms: number): Promise<void> {
    return new Promise<void>(resolve => {
      const timer = setTimeout(() => this.wakeCooldownSleep?.(), ms);
      this.wakeCooldownSleep = () => {
        clearTimeout(timer);
        this.wakeCooldownSleep = undefined;
        resolve();
      };
    });
  }

  private isRateLimited(): boolean {
    return this.rateLimitedUntil !== undefined && this.rateLimitedUntil > Date.now();
  }

  /**
   * True while the current access token can still be sent: not expired, with the foreground buffer left. Also true,
   * inside a cooldown, for a token the server minted less than a cooldown ago although it looks expired: the one the
   * backstop in renewAccessToken kept. Derived from the token's age rather than remembered, so it stops being
   * usable exactly when the cooldown ends, and a 429 on the next renewal holds callers instead of handing it out.
   */
  private hasUsableAccessToken(): boolean {
    const decodedToken = this.getDecodedAccessToken();
    if (!decodedToken) return false;
    // Signed with the rotated-out key: never usable, however long it has left.
    if (isIssuedBeforeKeyRotation(decodedToken)) return false;
    if (this.getTimeLeftInSecondsBeforeExpiration(decodedToken, this.secrets.offset) > FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS) return true;
    if (!this.isRateLimited() || typeof decodedToken.iat !== 'number') return false;
    const ageSeconds = Math.floor(Date.now() / 1000 - (this.secrets.offset ?? 0) / 1000) - decodedToken.iat;
    return ageSeconds < RATE_LIMITED_RETRY_IN_MS / 1000;
  }

  private checkTokenExpirationAndQueueRefresh = async (): Promise<AccessToken | null> => {
    // always grab the latest secrets from storage
    this.collectSavedSecrets();

    const decodedToken = this.getDecodedAccessToken();

    if (!decodedToken) {
      return this.attemptToRefreshTokenAndWrite();
    }

    // Signed with the rotated-out key: renew before handing it out, as for an expired token.
    if (isIssuedBeforeKeyRotation(decodedToken)) {
      this.reportPreRotationRenewal(decodedToken);
      return this.attemptToRefreshTokenAndWrite();
    }

    const timeLeft = this.getTimeLeftInSecondsBeforeExpiration(decodedToken, this.secrets.offset);

    // token is expired
    // applying a FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS buffer to help ensure the token is not expired when used
    if (timeLeft <= FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS) {
      // Token has effectively expired - refresh immediately
      return this.attemptToRefreshTokenAndWrite();
    }

    // Past this tab's own random point (60..120 seconds before expiry): refresh now, but return the
    // current token. It is still valid (more than FOREGROUND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS left).
    // The point, not the start of the window, is the trigger on token reads too: a fixed threshold here
    // would have every active tab renew in the same second again.
    const refreshBeforeExpiry = this.backgroundRefreshSecondsBeforeExpiry(this.secrets.accessToken?.id_token ?? '');
    if (timeLeft <= refreshBeforeExpiry) {
      this.attemptToRefreshTokenAndWrite();
      return Promise.resolve(this.secrets.accessToken || null);
    }

    // Otherwise fire the timer at that point.
    this.clearRefreshTimeoutId();
    this.refreshTimeoutId = globalWindow.setTimeout?.(
      () => {
        this.attemptToRefreshTokenAndWrite();
      },
      (timeLeft - refreshBeforeExpiry) * 1000,
    );

    return Promise.resolve(this.secrets.accessToken || null);
  };

  /**
   * Seconds before expiry at which `idToken`'s background refresh fires: a whole second drawn uniformly
   * from 60..120, once per token. See BACKGROUND_REFRESH_WINDOW_MIN_SECONDS for why.
   */
  private backgroundRefreshSecondsBeforeExpiry(idToken: string): number {
    if (this.backgroundRefresh?.idToken !== idToken) {
      const span = BACKGROUND_REFRESH_WINDOW_MAX_SECONDS - BACKGROUND_REFRESH_WINDOW_MIN_SECONDS + 1;
      this.backgroundRefresh = { idToken, secondsBeforeExpiry: Math.floor(BACKGROUND_REFRESH_WINDOW_MIN_SECONDS + Math.random() * span) };
    }
    return this.backgroundRefresh.secondsBeforeExpiry;
  }

  private broadcastTokenUpdate(nextToken: AccessToken | null): void {
    this.accessTokenSubject.next(nextToken);
  }

  private clearRefreshTimeoutId() {
    clearTimeout(this.refreshTimeoutId);
    this.refreshTimeoutId = 0;
  }

  /**
   * Helper to check if secrets object has all required fields for full functionality.
   * Complete secrets must have: accessToken, refreshToken, and clientIds.
   *
   * @param secrets - Parsed secrets object to validate
   * @returns true if secrets contain all required fields
   */
  private hasCompleteSecrets(secrets: Secrets): boolean {
    return !!(secrets.accessToken && secrets.refreshToken && secrets.accessTokenClientId);
  }

  /**
   * Collects auth secrets from storage with migration and reconciliation logic.
   *
   * **Priority Order:**
   * 1. Reads from primary storage (cookies)
   * 2. Falls back to localStorage if cookie secrets are incomplete
   * 3. Migrates complete localStorage secrets to cookies
   *
   * **Reconciliation Logic (TODO: Remove after migration confidence):**
   * - If cookie is missing or incomplete (lacks refreshToken/clientIds), checks localStorage
   * - If localStorage has complete secrets (refreshToken + clientIds), migrates them to cookies
   * - Does NOT validate accessToken freshness for migration (refresh flow handles expired tokens)
   * - This ensures users with partial cookies can still refresh tokens and stay logged in
   *
   * **Hash-Based Login:**
   * - Supports hash-based login flow (JOY-8012) for Firefox iframe restrictions
   * - Extracts secrets from URL hash when present
   */
  private collectSavedSecrets(): void {
    // Read from primary storage (cookies)
    let storedSecrets = this.getSecretsFromStorage();

    // Migration & reconciliation logic
    // TODO: Remove after migration confidence (1-2 months post-deployment)
    if (this.localStorage) {
      const cookieSecrets = storedSecrets ? this.parseSecretsStringToJSON(storedSecrets) : null;
      const isCookieIncomplete = !cookieSecrets || !this.hasCompleteSecrets(cookieSecrets);
      // A cookie holding a token from before the key rotation gives way to a localStorage session from after it
      // (signed in again on a legacy page, which writes only localStorage): no renewal needed, and it is the newer session.
      const isCookiePreRotation = !!cookieSecrets && this.isPreRotationSecrets(cookieSecrets);

      // If cookie is missing or incomplete, check localStorage
      if (isCookieIncomplete || isCookiePreRotation) {
        const localSecrets = this.localStorage.getItem('secrets');
        if (localSecrets) {
          const parsedLocalSecrets = this.parseSecretsStringToJSON(localSecrets);

          // Prefer localStorage if it has complete secrets (refreshToken + clientIds)
          // Even if accessToken is stale, the refresh flow will handle it using refreshToken
          // This ensures users with expired tokens but valid refreshToken can stay logged in
          if (this.hasCompleteSecrets(parsedLocalSecrets) && (isCookieIncomplete || !this.isPreRotationSecrets(parsedLocalSecrets))) {
            storedSecrets = localSecrets;
            // Migrate complete secrets to cookies for future reads
            this.setSecretsToStorage(localSecrets);
          }
        }
      }
    }

    const hash = windowExists() ? qs.parse(window.location.hash) : null;
    const hashSecrets = hash?.secrets;

    // This is to support the hash based login to fix ticket https://withjoy.atlassian.net/browse/JOY-8012
    // in addition to getSecretsForHashBasedLogin.
    if (hashSecrets) {
      this.secrets = this.parseSecretsStringToJSON(hashSecrets as string);
      if (!this.secrets.accessToken) {
        // We don't have a token in the hash, so something bad must have happened
        this.logger.refreshTokenError('Missing access token from hash based login', null);
      }

      // Remove the hash from the url and save the secrets to the storage
      delete hash.secrets;
      window.location.hash = qs.stringify(hash);
      history.replaceState('', document.title, window.location.pathname + window.location.search + window.location.hash);
      const serializedSecrets = this.prepareSecretsForWrite();
      this.setSecretsToStorage(serializedSecrets);
      // Sync to localStorage for backwards compatibility
      if (this.localStorage) {
        this.localStorage.setItem('secrets', serializedSecrets);
      }
    }

    if (storedSecrets) {
      this.secrets = this.parseSecretsStringToJSON(storedSecrets);
    }

    if (!this.secrets) {
      this.secrets = {};
    }
  }

  /**
   * This method is being created to support the hash based login to fix ticket https://withjoy.atlassian.net/browse/JOY-8012
   * where Firefox users are not being able to use the add to joy snippet. Since the snippet is loaded inside an iframe,
   * due to new restrictions in Firefox, the cookies are not being read by this iframe. So we'll be sending the secrets
   * to the app using the hash in the url. The app will read the hash and use the secrets to login the user.
   * @returns a string that can be used to login to the app with a hash (empty string if there are no secrets)
   */
  public getSecretsForHashBasedLogin(): string {
    // Check if secrets object has any meaningful content (accessToken is the key field)
    return this.secrets?.accessToken ? '#secrets=' + encodeURIComponent(JSON.stringify(this.secrets)) : '';
  }

  private getDecodedAccessToken(): DecodedToken | null {
    if (this.secrets.accessToken) {
      try {
        const { id_token } = this.secrets.accessToken;
        if (!id_token) {
          console.warn('No token to decode.');
          return null;
        }
        // Both environments: tolerant base64url-aware decode (JWT payloads
        // are base64url, which the old client-side raw atob rejected).
        return decodeJwtPayload(id_token);
      } catch (e) {
        console.error(`Unable to decode token.`);
        console.error(e);
        return null;
      }
    }
    return null;
  }

  private getAccessToken(): string | null {
    if (this.secrets.accessToken) {
      // return this.isTokenExpired() ? null : this.secrets.accessToken.id_token;
      // Agreeing to send the token even if it's expired as we try to find out why the token is not refreshing
      return this.secrets.accessToken.id_token;
    } else {
      return null;
    }
  }

  private getTimeLeftInSecondsBeforeExpiration(decodedToken: DecodedToken, offset: number = 0): number {
    // The following logic is extracted from our current JWT module
    return decodedToken.exp - Math.floor((Date.now() - offset) / 1000);
  }

  /**
   * Checks whether the current access token exists and has enough remaining TTL
   * to be usable for an eager auth query (e.g., GetMe during SSR or initial CSR render).
   * Uses the same threshold as parseAuthCookie (~16.67 min) to ensure consistency
   * between SSR and CSR auth paths.
   *
   * Returns false if: no token, token can't be decoded, or TTL < threshold.
   * When false, callers should fall back to the async getTokenAsync() path
   * which can refresh the token.
   */
  public hasValidTokenForEagerAuth(): boolean {
    const decodedToken = this.getDecodedAccessToken();
    if (!decodedToken || isIssuedBeforeKeyRotation(decodedToken)) {
      return false;
    }
    const timeLeft = this.getTimeLeftInSecondsBeforeExpiration(decodedToken, this.secrets.offset);
    return timeLeft >= BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS;
  }

  /**
   * Parses auth cookie string for SSR usage.
   *
   * **Cookie Format Evolution:**
   * - Legacy: Contains only AccessToken (for SSR compatibility)
   * - Current: Transitioning to full Secrets object
   * - Future: Will always contain full Secrets object
   *
   * **SSR Considerations:**
   * - Extracts AccessToken for authentication header
   * - Calculates TTL to validate token freshness
   * - Uses same threshold as background token refresh (BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS)
   * - Prevents hydration mismatches where server renders auth content but client sees expired token
   *
   * @param stringifiedToken - URL-encoded JSON string from cookie (may be AccessToken or full Secrets)
   * @param options - Configuration options for parsing
   * @param options.minTtlSeconds - Minimum TTL in seconds for token to be considered valid (default: 1000s = ~16.67 min)
   * @returns Parsed token and TTL if valid, or undefined if invalid/missing/expired
   */
  public static parseAuthCookie(
    stringifiedToken: string | null | undefined,
    options?: { minTtlSeconds?: number },
  ): { token: AccessToken; ttl: number; clientId?: string; refreshToken?: string; offset?: number } | undefined {
    if (!stringifiedToken) {
      return;
    }

    const minTtlSeconds = options?.minTtlSeconds ?? BACKGROND_REFRESH_TOKEN_BEFORE_EXPIRE_IN_SECONDS;

    let token: AccessToken | undefined = undefined;
    let clientId: string | undefined;
    let refreshToken: string | undefined;
    let offset: number | undefined;
    let ttl: number;

    try {
      const parsed = JSON.parse(stringifiedToken);

      // Handle both formats: AccessToken directly or Secrets object with accessToken property
      if ((parsed.token_type || parsed.bearer_type) && parsed.id_token) {
        // Legacy format: just AccessToken (Auth0 uses token_type, app uses bearer_type)
        token = parsed as AccessToken;
      } else if (parsed.accessToken) {
        // New format: full Secrets object — extract all fields for SSR LoginManager seeding
        token = typeof parsed.accessToken === 'string' ? JSON.parse(parsed.accessToken) : parsed.accessToken;
        clientId = parsed.accessTokenClientId;
        refreshToken = parsed.refreshToken;
        offset = parsed.offset;
      } else {
        console.error('Invalid auth cookie format: missing token data');
        return;
      }

      if (!token || !token.id_token) {
        console.error('Invalid token: missing id_token');
        return;
      }

      // Both environments: tolerant base64url-aware decode (JWT payloads
      // are base64url, which the old client-side raw atob rejected).
      const decodedToken = decodeJwtPayload(token.id_token);

      // Signed with the rotated-out key: render the anonymous view and let the client renew it.
      if (isIssuedBeforeKeyRotation(decodedToken)) {
        console.warn('Auth token was issued before the key rotation; not using it for SSR');
        return;
      }

      ttl = Math.floor(decodedToken.exp - Date.now() / 1000);

      // Reject tokens that expire too soon to prevent SSR/client hydration mismatches
      if (ttl < minTtlSeconds) {
        console.warn(`Auth token TTL (${ttl}s) is less than minimum required (${minTtlSeconds}s) for SSR`);
        return;
      }
    } catch (error) {
      console.error('Malformed SSR auth token', error);
      return;
    }

    return { token, ttl, clientId, refreshToken, offset };
  }

  private parseSecretsStringToJSON(secretsString: string): Secrets {
    try {
      const parsed = JSON.parse(secretsString);

      // Check if this is a cookie format (just AccessToken) vs localStorage format (full Secrets)
      if ((parsed.token_type || parsed.bearer_type) && parsed.id_token) {
        // Cookie format: just the AccessToken object (Auth0 uses token_type, app uses bearer_type)
        return { accessToken: parsed };
      }

      // localStorage format: full Secrets object
      const secrets = parsed;

      // we used to stringify access token itself so need to handle that case for backward compat
      // probably can remove this code after July 2020 (3 months from now)
      let accessToken = secrets.accessToken;
      if (typeof secrets.accessToken === 'string') {
        accessToken = JSON.parse(accessToken);
      }

      accessToken = accessToken || null;

      return { ...secrets, accessToken };
    } catch (e) {
      return {};
    }
  }

  private writeToSecretsAndAlertListeners(newToken: AccessToken | null, clientId?: string | null, refreshToken?: string, serverTime?: number): void {
    this.secrets.accessToken = newToken || undefined;

    if (refreshToken) {
      this.secrets.refreshToken = refreshToken;
    }

    if (clientId) {
      this.secrets.accessTokenClientId = clientId;
      this.secrets.refreshTokenClientId = clientId;
    }

    if (serverTime) {
      this.secrets.offset = Date.now() - serverTime;
    }

    const serializedSecrets = this.prepareSecretsForWrite();

    // Write to primary storage (cookies)
    this.setSecretsToStorage(serializedSecrets);

    // Sync to localStorage for backwards compatibility
    // TODO: Remove after migration confidence (1-2 months post-deployment)
    if (this.localStorage) {
      this.localStorage.setItem('secrets', serializedSecrets);
    }

    this.broadcastTokenUpdate(newToken);
  }

  private prepareSecretsForWrite = (): string => {
    const { accessToken, ...restSecrets } = this.secrets;

    const withStringifiedAccessToken: PersistedSecrets = {
      ...restSecrets,
    };

    try {
      // All apps expect `accessToken` to be stringified when reading from local storage
      withStringifiedAccessToken.accessToken = JSON.stringify(accessToken);
    } catch (e) {
      this.logger.ensureSecretsFormatError(e);
    }
    return JSON.stringify(withStringifiedAccessToken);
  };

  /**
   * Returns the storage key based on the storage type.
   * Cookies use 'SsrAuthToken' (with 'joy' prefix → 'joySsrAuthToken') for SSR compatibility.
   * localStorage uses 'secrets' for backwards compatibility.
   */
  private getStorageKey(): string {
    return this.storage instanceof CookieStorage ? SSR_TOKEN_KEY : LOCAL_STORAGE_TOKEN_KEY;
  }

  /**
   * Reads auth secrets from primary storage (cookies or localStorage fallback).
   * Uses storage-specific key: 'joySsrAuthToken' for cookies, 'secrets' for localStorage.
   */
  private getSecretsFromStorage(): string | null | undefined {
    const key = this.getStorageKey();
    return this.storage.getItem(key);
  }

  /**
   * Writes auth secrets to primary storage (cookies or localStorage fallback).
   *
   * **Storage Format:**
   * - Cookies: stores FULL Secrets object (for cookie-primary future)
   * - localStorage: stores FULL Secrets object
   *
   * **Rationale:** Cookies must contain complete secrets (refreshToken, clientIds, offset)
   * to function as primary storage. Storing only AccessToken would break token refresh.
   *
   * **Cookie Expiration:**
   * - Sets max-age to match access token's expires_in value (CookieStorage only)
   * - Aligns with industry best practices (NextAuth.js, Auth0 SDK, OAuth/OIDC)
   * - Prevents premature logout when browser closes (session cookies)
   * - Cookie and token lifetimes stay synchronized
   * - localStorage and MemoryStorage ignore the options parameter
   *
   * Uses storage-specific key: 'joySsrAuthToken' for cookies, 'secrets' for localStorage.
   * Note: Call sites should explicitly sync to localStorage for backwards compatibility.
   */
  private setSecretsToStorage(value: string): void {
    const key = this.getStorageKey();

    // Parse the secrets being written to extract expires_in for cookie max-age
    // parseSecretsStringToJSON returns {} on parse errors, so this never throws
    const parsedSecrets = this.parseSecretsStringToJSON(value);
    // Note: During migration, old tokens near expiry will still get full max-age (e.g., 86400s)
    // despite having less remaining time. This desync is temporary and self-heals on next token
    // refresh (<24h), affecting only one-time localStorage→cookie migration, not ongoing operation.
    const expiresIn = parsedSecrets.accessToken?.expires_in;

    // Pass options if expires_in exists (CookieStorage uses it, others ignore it)
    this.storage.setItem(key, value, expiresIn ? { maxAgeInSeconds: expiresIn } : undefined);
  }

  /**
   * Deletes auth secrets from primary storage (cookies or localStorage fallback).
   * Uses storage-specific key: 'joySsrAuthToken' for cookies, 'secrets' for localStorage.
   * Note: Call sites should explicitly sync deletion to localStorage for backwards compatibility.
   */
  private deleteSecretsFromStorage(): void {
    const key = this.getStorageKey();
    this.storage.setItem(key, undefined);
  }
}
